import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { BlobServiceClient } from '@azure/storage-blob';
import { Observable } from 'rxjs';

import { UploadConfiguration } from '../models/upload-configuration.model';

/**
 * Upload service for communicating with the API backend and Azure Blob.
 *
 * @export
 * @class UploadsService
 */
@Injectable({
  providedIn: 'root'
})
export class UploadService {
  /**
   * Creates an instance of UploadService.
   *
   * @param {HttpClient} http Angular HTTP service.
   * @param {string} baseApiUrl Base API URL.
   * @memberof SalesService
   */
  constructor(
    protected http: HttpClient,
    @Inject('BASE_API_URL') protected baseApiUrl: string
  ) { }

  /**
   * Get the Shared Access Signature (SAS) for a specified path.
   *
   * @param {string} id The ID of the parent record.
   * @param {string} fileName The name of the uploaded file.
   * @return {Observable<string>} The SAS and associated upload configuration.
   * @memberof UploadService
   */
  public getSas(id: string, fileName: string): Observable<UploadConfiguration> {
    return this.http.get<UploadConfiguration>(`${this.baseApiUrl}/sales/${id}/blob/sas?fileName=${fileName}`)
  }

  /**
   * Upload a file to Azure Blob.
   *
   * @param {UploadConfiguration} config The SAS and associated upload configuration.
   * @param {*} file The file to upload.
   * @return {boolean} Whether successful or not.
   * @memberof UploadService
   */
  async uploadFile(config: UploadConfiguration, file: any) {
    if (!config.account || !config.container || !config.path || !config.token) {
      return false;
    }

    const blobServiceClient = new BlobServiceClient(`https://${config.account}.blob.core.windows.net?${config.token}`);
    const containerClient = blobServiceClient.getContainerClient(config.container);
    const blockBlobClient = containerClient.getBlockBlobClient(config.path);

    await blockBlobClient.uploadData(file, {
      maxSingleShotSize: 4 * 1024 * 1024,
      blobHTTPHeaders: {
        blobContentType: config.fileType
      }
    });

    return true;
  }
}
